import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useAuthenticatedApi } from "../../hooks/useAuthenticatedApi";
import { apiEndpoints } from "../../utils/api";

// Import styles
import { injectCodeEditorScrollbarStyles } from "./styles";

// Import hooks
import { useEditorTheme } from "./hooks/useEditorTheme";
import { useWorkspace } from "./hooks/useWorkspace";
import { useFileOperations } from "./hooks/useFileOperations";
import { useTabManagement } from "./hooks/useTabManagement";
import { useNotification } from "./hooks/useNotification";
import { useReadOnlyMode } from "../../hooks/useReadOnlyMode";

// Import components
import FileExplorer from "./components/FileExplorer";
import EditorTabs from "./components/EditorTabs";
import EditorArea from "./components/EditorArea";
import Notifications from "./components/Notifications";
import CommitDialog from "./components/Dialogs/CommitDialog";
import CollapsibleSidebar from "../common/CollapsibleSidebar";

/**
 * Main CodeEditorPanel component
 * Refactored to use modular components and hooks
 */
const CodeEditorPanel = forwardRef(
  ({ workspaceId, projectPath, onCommitSuccess = null }, ref) => {
    const { isReadOnly } = useReadOnlyMode();
    const authenticatedApi = useAuthenticatedApi();

    // Inject scrollbar styles on mount
    useEffect(() => {
      injectCodeEditorScrollbarStyles();
    }, []);

    // Custom hooks
    const { isDarkMode } = useEditorTheme();
    const { workspace, isSandbox } = useWorkspace(
      workspaceId,
      authenticatedApi,
    );
    const {
      notification,
      notificationType,
      showNotification,
      clearNotification,
      showSuccess,
      showError,
      showWarning,
    } = useNotification();

    const {
      files,
      isLoading,
      fileError,
      setFileError,
      loadingDirectories,
      loadingFile,
      expandedFolders,
      loadFileContent,
      saveFile,
      toggleFolder,
      refreshFiles,
    } = useFileOperations(workspaceId, authenticatedApi, projectPath);

    const {
      openTabs,
      activeTab,
      modifiedFiles,
      fileContent,
      isDirty,
      openTab,
      closeTab,
      switchToTab,
      handleContentChange,
      markAsSaved,
    } = useTabManagement();

    // State for commit dialog and saving
    const [showCommitDialog, setShowCommitDialog] = useState(false);
    const [savingFile, setSavingFile] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    // Handle file selection
    const handleFileSelect = useCallback(
      async (file) => {
        if (!file || file.type !== "file") return;

        // Check if we can open a new tab first (if not already open)
        const isAlreadyOpen = openTabs.some((tab) => tab.path === file.path);
        if (!isAlreadyOpen && openTabs.length >= 5) {
          showWarning(
            "Maximum of 5 files can be open at once. Please close a tab first.",
          );
          return;
        }

        try {
          setSelectedFile(file.path);

          // If the tab is already open, just switch to it
          if (isAlreadyOpen) {
            switchToTab(file.path);
            return;
          }

          // Show loading state immediately
          const loadingContent = "...";
          openTab(file, loadingContent);

          // Load actual content
          const content = await loadFileContent(file.path);

          // Update the tab with actual content
          const updatedFile = { ...file, content };
          openTab(updatedFile, content);
        } catch (err) {
          console.error("Error selecting file:", err);
          showError(`Error loading file: ${err.message}`);

          // Update tab with error content
          const errorContent = `// Error loading file: ${err.message}`;
          openTab(file, errorContent);
        }
      },
      [openTabs, openTab, switchToTab, loadFileContent, showError, showWarning],
    );

    // Handle save action
    const handleSave = useCallback(async () => {
      if (isReadOnly) return;
      if (!activeTab || !isDirty) return;

      try {
        setSavingFile(true);

        // Save the file
        await saveFile(activeTab.path, fileContent);

        // Mark as saved
        markAsSaved();

        // Show appropriate notification
        if (isSandbox) {
          showSuccess("File saved (will be committed automatically)");
        } else {
          showSuccess("File saved successfully");
        }
      } catch (error) {
        showError(`Error saving file: ${error.message}`);
      } finally {
        setSavingFile(false);
      }
    }, [
      isReadOnly,
      activeTab,
      isDirty,
      fileContent,
      saveFile,
      markAsSaved,
      isSandbox,
      showSuccess,
      showError,
    ]);

    // Handle commit (for non-sandbox workspaces)
    const handleCommit = useCallback(
      async (message) => {
        if (isReadOnly) return;
        if (!activeTab) return;

        try {
          setSavingFile(true);
          await saveFile(activeTab.path, fileContent, message);
          markAsSaved();
          setShowCommitDialog(false);
          showSuccess("File saved and committed successfully");

          // Callback for parent component
          if (onCommitSuccess) {
            setTimeout(() => {
              onCommitSuccess();
            }, 1000);
          }
        } catch (error) {
          showError(`Error committing file: ${error.message}`);
        } finally {
          setSavingFile(false);
        }
      },
      [
        isReadOnly,
        activeTab,
        fileContent,
        saveFile,
        markAsSaved,
        onCommitSuccess,
        showSuccess,
        showError,
      ],
    );

    // Handle refresh
    const handleRefresh = useCallback(async () => {
      try {
        setFileError(null);
        await refreshFiles();
      } catch (err) {
        setFileError(err.message);
      }
    }, [refreshFiles, setFileError]);

    // Expose refresh method to parent
    useImperativeHandle(
      ref,
      () => ({
        refresh: handleRefresh,
      }),
      [handleRefresh],
    );

    // Keyboard shortcut for saving
    useEffect(() => {
      const handleKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "s") {
          e.preventDefault();
          handleSave();
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [handleSave]);

    return (
      <div className="flex flex-col h-full overflow-hidden relative">
        {/* Notifications */}
        <Notifications
          notification={notification}
          notificationType={notificationType}
          onClear={clearNotification}
        />

        {/* Main Content */}
        <main className="flex-1 flex flex-col overflow-hidden">
          <div className="bg-gray-50 dark:bg-neutral-900 flex-1 flex overflow-hidden">
            <div className="flex h-full overflow-hidden w-full">
              {/* File Explorer Sidebar */}
              <CollapsibleSidebar
                width={256}
                className="bg-white dark:bg-neutral-800 border-r border-neutral-200 dark:border-neutral-700"
              >
                <FileExplorer
                  files={files}
                  isLoading={isLoading}
                  fileError={fileError}
                  onRefresh={handleRefresh}
                  onFileSelect={handleFileSelect}
                  onDirectoryToggle={toggleFolder}
                  expandedFolders={expandedFolders}
                  selectedFile={selectedFile}
                  loadingDirectories={loadingDirectories}
                  loadingFile={loadingFile}
                  projectPath={projectPath}
                />
              </CollapsibleSidebar>

              {/* Code Editor Area */}
              <div className="flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-neutral-900">
                {/* Tabs Bar - only show if there are open tabs */}
                {openTabs.length > 0 && (
                  <EditorTabs
                    openTabs={openTabs}
                    activeTab={activeTab}
                    modifiedFiles={modifiedFiles}
                    onTabSwitch={switchToTab}
                    onTabClose={closeTab}
                  />
                )}

                {/* Editor Content */}
                <div
                  className={`flex-1 bg-gray-50 dark:bg-neutral-900 ${activeTab ? "font-mono text-sm" : ""}`}
                >
                  <EditorArea
                    activeTab={activeTab}
                    fileContent={fileContent}
                    isDirty={isDirty}
                    isSaving={savingFile}
                    isDarkMode={isDarkMode}
                    onContentChange={handleContentChange}
                    onSave={handleSave}
                    isReadOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Commit Dialog (for non-sandbox workspaces) */}
        {showCommitDialog && (
          <CommitDialog
            isOpen={showCommitDialog}
            onClose={() => setShowCommitDialog(false)}
            onCommit={handleCommit}
            fileName={activeTab?.name}
          />
        )}
      </div>
    );
  },
);

CodeEditorPanel.displayName = "CodeEditorPanel";

export default CodeEditorPanel;
